import React from 'react';

import type { Text } from '@zillow/constellation';
import { AdornedInput, Adornment, Label, Spacer } from '@zillow/constellation';
import { CurrencyInput } from '@zillow/zrs-comet';

import type { Section } from '../../../../models/index';
import type { HTMLElementEvent } from '../../../../models/Types';
import { SEPARATE_HEATING_CHARGE } from '../../../../constants';
import SwitchWithTitle from '../../../Widgets/SwitchWithTitle';
import { CenteredHR } from '../../../Widgets/CenteredHR';
import HeaderToggle from '../Utilities/HeaderToggle';
import { CURRENCY_INPUT_PLACEHOLDER } from '../../../../constants/stringConstants';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function SeparateHeatingCharge_v4({ section, setSectionValues }: Props) {
  const heatingChargeApplies = section.getFieldValue(SEPARATE_HEATING_CHARGE.FIELD_NAME.IS_ENABLED);
  const paysInAdditionToRent = section.getFieldValue(
    SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
  );
  const fixedAmountValue = section.getFieldValue(SEPARATE_HEATING_CHARGE.FIELD_NAME.FIXED_AMOUNT_VALUE);

  function toggleUtility(): void {
    setSectionValues({
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.IS_ENABLED]: !section.getFieldValue(
        SEPARATE_HEATING_CHARGE.FIELD_NAME.IS_ENABLED,
      ),
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: false,
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.FIXED_AMOUNT_VALUE]: '',
    });
  }

  function togglePaysInAddition(): void {
    setSectionValues({
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT]: !section.getFieldValue(
        SEPARATE_HEATING_CHARGE.FIELD_NAME.TENANT_PAYS_IN_ADDITION_TO_RENT,
      ),
    });
  }

  function setFixedAmountValue(event: HTMLElementEvent<typeof Text>): void {
    setSectionValues({
      [SEPARATE_HEATING_CHARGE.FIELD_NAME.FIXED_AMOUNT_VALUE]: event.currentTarget.value,
    });
  }

  return (
    <>
      <HeaderToggle
        header="Is heat provided separately from other utilities?"
        subheader="Applies to this property"
        switchOn={heatingChargeApplies}
        onSwitchToggle={toggleUtility}
      />
      {heatingChargeApplies && (
        <>
          <Spacer marginBottom="sm">
            <CenteredHR />
            <SwitchWithTitle
              text="Tenant pays in addition to base rent"
              onClick={togglePaysInAddition}
              toggled={paysInAdditionToRent}
            />
          </Spacer>
          {paysInAdditionToRent && (
            <Spacer marginTop="sm">
              <Label>What is the additional fixed fee for heat?</Label>
              <AdornedInput
                inputId="AdornedInput-fixed-amount-value"
                leftAdornment={<Adornment>$</Adornment>}
                rightAdornment={<Adornment>per month</Adornment>}
                input={
                  <CurrencyInput
                    aria-label="fixed amount"
                    data-test="fixedAmountValue"
                    placeholder={CURRENCY_INPUT_PLACEHOLDER}
                    value={fixedAmountValue || ''}
                    onBlur={setFixedAmountValue}
                    onChange={setFixedAmountValue}
                  />
                }
              />
            </Spacer>
          )}
        </>
      )}
    </>
  );
}

SeparateHeatingCharge_v4.description = 'Fixed fee value instead of asking landord to bill tenant actual cost';

export default SeparateHeatingCharge_v4;
